/**
 * Broadcast primitives — the shared vocabulary every screen is built from.
 */

import { useState, type ReactNode } from 'react'
import type { StatusChip } from './engine/types'
import { isApple, isStandalone } from './push'
import { flush, useStore } from './store'

/**
 * Is this phone talking to the server, or holding on to what you typed?
 *
 * The app used to say nothing at all when everything was fine, which is the
 * right instinct for notifications and the wrong one here: this is the only
 * record of the round, and "no complaint" is not the same as "your scores are
 * safe". So there is now always a light, in the same corner of every screen.
 *
 * Four states, because the app genuinely has four:
 *
 *   green            everything typed here is on the server
 *   green + count    sending right now — healthy, and usually gone in a second
 *   amber + count    no service; scores are held on this phone and nothing is lost
 *   red              the server refused something, and somebody has to retype it
 *
 * Steady green rather than blinking green, deliberately. A light that blinks
 * all week is a light nobody sees by Wednesday, and it would be blinking to
 * report the one state that needs no attention. Amber and red breathe instead —
 * motion belongs on the states worth looking at.
 *
 * Color never carries the meaning alone: the count is on the amber light, an
 * exclamation on the red one, and tapping any of them says it in words.
 */
function SyncDot() {
  const token = useStore((s) => s.token)
  const online = useStore((s) => s.online && s.reachable)
  const pending = useStore((s) => s.outbox.length)
  const error = useStore((s) => s.error)
  const lastSyncAt = useStore((s) => s.lastSyncAt)
  const [open, setOpen] = useState(false)

  // Nothing to report before anyone has signed in.
  if (!token) return null

  const state = error ? 'error' : !online ? 'offline' : pending > 0 ? 'sending' : 'live'
  const dot =
    state === 'error'
      ? 'bg-signal bc-breathe'
      : state === 'offline'
        ? 'bg-[#E0A33E] bc-breathe'
        : 'bg-[#4CAF6D]'

  const words =
    state === 'error'
      ? error
      : state === 'offline'
        ? pending > 0
          ? `No service. ${pending} score${pending === 1 ? '' : 's'} held on this phone — they go up by themselves the moment there is signal. Nothing is lost by closing the app.`
          : 'No service. Keep scoring; everything is saved here and goes up by itself.'
        : state === 'sending'
          ? `Sending ${pending} score${pending === 1 ? '' : 's'}…`
          : lastSyncAt
            ? `Everything on this phone is on the server. Last checked ${new Date(lastSyncAt).toLocaleTimeString()}.`
            : 'Connected.'

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Sync status: ${state}`}
        className="bc-tap shrink-0 flex items-center gap-1.5 -mr-2 pr-2"
      >
        <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
        {state === 'error' && <span className="bc-label">!</span>}
        {state !== 'error' && pending > 0 && <span className="bc-label">{pending}</span>}
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-20 bg-ink text-paper px-4 py-3 border-t border-paper/20">
          <p className="text-[13px] leading-snug">{words}</p>
          {online && pending > 0 && (
            <button
              type="button"
              onClick={() => void flush()}
              className="bc-tap bc-label border-2 border-paper px-4 py-2 mt-2"
            >
              Send now
            </button>
          )}
        </div>
      )}
    </>
  )
}

export function Bar({
  kicker,
  title,
  sub,
  subLg,
  live,
  onTitle,
  figure,
}: {
  kicker?: string
  title: string
  sub?: string
  /**
   * Half again bigger sub-line. The Score masthead's sub is the par, the
   * yardage and the stroke index — read standing over a ball, in wind, and
   * Scott's ruling is that it earns more size than a caption.
   */
  subLg?: boolean
  live?: boolean
  /** Makes the title itself the control — used to jump between holes. */
  onTitle?: () => void
  /**
   * A huge figure riding the masthead's right side — the hole number, alone.
   * Scott, sequencing holes on the course: the header barely changed between
   * taps, so nothing told his eye the hole had. A 56px numeral changes.
   * Rendered as part of the same tap target when onTitle is set.
   */
  figure?: string
}) {
  return (
    <header className="relative bg-ink text-paper px-4 pt-safe pb-4">
      <div className="flex items-start gap-3 pt-3">
        {/*
          The crest rides on its own cream field rather than being knocked out
          to a single color. It is embroidery — dark outline over tan fill —
          so a silhouette of it is a blob, and dropping it straight onto the
          dark bar would lose the outline that gives it its shape. A patch is
          what it is in life, and a patch is what reads here.
        */}
        <img
          src="/crest-mark.png"
          alt=""
          className="w-11 h-11 rounded shrink-0 select-none"
          draggable={false}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-h-[1rem]">
            {live && (
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-onink-signal rounded-full" />
                <span className="bc-label">Live</span>
              </span>
            )}
            {kicker && (
              <span className={`bc-label text-onink ${figure ? '' : 'truncate'}`}>{kicker}</span>
            )}
          </div>
          {onTitle ? (
            <button
              type="button"
              onClick={onTitle}
              className={`bc-display ${figure ? 'text-xl' : 'text-3xl'} mt-0.5 bc-tap flex items-baseline gap-2 text-left`}
            >
              {title}
              {/* A quiet affordance: the title has to look tappable without
                  turning the masthead into a toolbar. */}
              <span className="text-base leading-none opacity-60">&#9662;</span>
            </button>
          ) : (
            <h1 className="bc-display text-3xl mt-0.5">{title}</h1>
          )}
          {sub && (
            <p
              className={`bc-label bc-sub text-onink mt-1 ${
                subLg ? 'bc-sub-lg whitespace-nowrap' : ''
              }`}
            >
              {sub}
            </p>
          )}
        </div>
        {figure && (
          <button
            type="button"
            onClick={onTitle}
            aria-hidden={onTitle ? undefined : true}
            tabIndex={-1}
            className="bc-display text-[56px] leading-none bc-tap absolute right-4 bottom-4"
          >
            {figure}
          </button>
        )}
        {/* Same corner on every screen, so it is somewhere to look rather than
            something to find. */}
        <SyncDot />
      </div>
    </header>
  )
}

export function SectionRule({ children }: { children: ReactNode }) {
  return (
    <h2 className="bc-label border-t-[3px] border-ink pt-2 mx-4 mt-5 mb-1">{children}</h2>
  )
}

export function Chip({ chip }: { chip: StatusChip }) {
  return (
    <div className="text-right">
      <div className="bc-num text-base leading-none">{chip.primary}</div>
      {/* Inherits from the row so it stays legible on the leader's inverted
          ink background as well as on paper. */}
      <div className="text-[12px] opacity-70 font-semibold uppercase tracking-wider mt-0.5">
        {chip.secondary}
      </div>
    </div>
  )
}

/** A leaderboard row. The leader inverts to solid black — the telecast tell. */
export function Row({
  pos,
  name,
  wrap,
  sub,
  tag,
  right,
  lead,
  onClick,
}: {
  pos?: ReactNode
  name: string
  /**
   * Let the name run to as many lines as it needs. The default truncates,
   * right for a leaderboard of short names and wrong for a slip whose
   * description IS the bet — "Springer to tell at least 3 stor…" is not a
   * record of what a man wagered.
   */
  wrap?: boolean
  /** Who it was against — a result without an opponent is half a result. */
  sub?: string
  tag?: string
  right: ReactNode
  lead?: boolean
  onClick?: () => void
}) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      {...(onClick ? { onClick, type: 'button' as const } : {})}
      className={`w-full text-left grid grid-cols-[22px_minmax(0,1fr)_auto_auto] gap-3 items-center px-4 py-2.5 border-b border-rule bc-tap ${
        lead ? 'bg-ink text-paper' : ''
      }`}
    >
      <span className="bc-num text-xs text-right">{pos}</span>
      <span className="min-w-0">
        <span className={`bc-name text-sm block ${wrap ? 'leading-snug' : 'truncate'}`}>{name}</span>
        {sub && (
          <span className={`text-[12px] truncate block ${lead ? 'text-paper/70' : 'text-mute'}`}>
            {sub}
          </span>
        )}
      </span>
      {tag ? (
        <span
          /*
            Through the label token, not hand-set to 800.

            In the live South Coast theme `.bc-label` is 650 and `.bc-display`
            is 700, so a hand-set `font-extrabold` made these chips — "F1",
            "OPEN" — heavier than the 30px masthead title above them. That is
            the inverted hierarchy the tokens exist to prevent, and the reason
            it survived is that the tag never went through one: it also did not
            follow the theme, staying 800 in all five.

            `bc-chip` puts the tracking back where `tracking-wider` had it. A
            chip is the one label too narrow for 0.2em — see index.css.
          */
          className={`bc-label bc-chip text-[11px] px-1.5 py-0.5 ${
            lead ? 'bg-signal text-paper' : 'bg-wash text-mute'
          }`}
        >
          {tag}
        </span>
      ) : (
        <span />
      )}
      {right}
    </Tag>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="px-4 py-10 text-center text-mute text-sm">{children}</p>
}

/**
 * Shown wherever an unverified course is in play. Deliberately hard to ignore:
 * a wrong stroke index silently corrupts every net score for the whole week.
 */
export function UnverifiedBanner({ courseName }: { courseName: string }) {
  return (
    <div className="bg-signal text-paper px-4 py-2.5 flex items-start gap-2">
      <span className="bc-label mt-0.5">!</span>
      <p className="text-xs leading-snug">
        <b className="bc-name">{courseName}</b> has not been checked against the real
        scorecard. Stroke index decides every pop, and a transposed one is invisible
        all week. Confirm it in Setup and this goes away.
      </p>
    </div>
  )
}

export function SyncPill({
  online,
  pending,
  error,
  onDismiss,
}: {
  online: boolean
  pending: number
  error: string | null
  onDismiss?: () => void
}) {
  if (error)
    return (
      // Tappable, because some of these are standing instructions — "that score
      // was dropped, write it down again" has to survive the next good sync,
      // and something that survives on its own needs a way to be put away.
      //
      // `bc-tap` for the same reason every other control has it: at 11.5px on
      // py-1.5 this was 29px tall, and it is the one control in the app that is
      // only ever reached by somebody who is already annoyed. A dismiss that
      // needs two attempts reads as the app arguing back.
      <button
        type="button"
        onClick={onDismiss}
        className="bc-tap w-full text-left bg-signal text-paper px-4 py-1.5 bc-label"
      >
        {error} — tap to dismiss
      </button>
    )
  /*
   * Offline is a card, not a line.
   *
   * The thin strip this replaced was accurate and reassured nobody. Someone
   * standing on the 7th with no bars needs three questions answered before they
   * will keep entering scores: is it broken, will I lose this, and do I have to
   * do anything. A one-line status answers none of them, and the cost of
   * getting this wrong is a group that stops trusting the app and goes back to
   * the paper card.
   */
  if (!online)
    return (
      <div className="bg-ink text-paper px-4 py-3">
        <p className="bc-label flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-onink-signal rounded-full" />
          No signal
        </p>
        <p className="text-[13px] leading-snug mt-1 text-onink">
          Keep scoring. {pending > 0 ? `${pending} ` : 'Everything you enter is '}
          {pending > 0
            ? `score${pending === 1 ? '' : 's'} held on this phone — they go up by themselves the moment there is service.`
            : 'saved here and goes up by itself the moment there is service.'}{' '}
          Nothing is lost by closing the app.
        </p>
      </div>
    )
  /*
   * "Sending…" is the status light's job now.
   *
   * It was a full-width strip that appeared for a second or two after every
   * tap, pushing the page down and pulling it back — motion at the top of the
   * screen for the most ordinary thing the app does. The dot in the masthead
   * carries the same count without moving anything.
   *
   * The offline card above stays. It is three lines of reassurance for somebody
   * who has just watched the bars disappear, and a dot they have to know to tap
   * is not a substitute for it.
   */
  return null
}

export function TabBar({
  tab,
  onTab,
  flag,
}: {
  tab: string
  onTab: (t: string) => void
  /**
   * A tab id to mark as wanting attention.
   *
   * One id rather than a count or a set, deliberately. A number here would be a
   * second place that has to agree with the screen it points at, and five tabs
   * that can all light up is a christmas tree nobody reads. The dot says "there
   * is something here"; the screen says what.
   */
  flag?: string | null
}) {
  // Setup is always reachable — it asks for the PIN itself rather than hiding,
  // because a tab that vanishes reads as a bug to whoever knows the code.
  //
  // One label, both ways. It read "Phone" to anyone without the PIN, which is
  // where the alert switches and the install prompt live — and nobody went
  // there, because "Phone" names the hardware rather than anything you might
  // want to do. "Setup" is what the tab is called when you have the code, what
  // the rest of the app calls it in prose, and the word somebody actually looks
  // for when their birdie alerts are not arriving.
  const tabs = [
    ['score', 'Score'],
    ['board', 'Board'],
    ['cards', 'Cards'],
    ['odds', 'Odds'],
    ['admin', 'Setup'],
  ]
  return (
    <nav
      /*
       * z-30 because the sticky first columns in the score grid and the cards
       * are positioned too, and without an explicit order they win the paint —
       * an opaque name cell was drawing straight over the SCORE tab, so the
       * navigation vanished exactly on the two screens that scroll sideways.
       */
      /*
       * FIXED, not sticky. Sticky pins an element inside its own parent's
       * box, and this nav is the last child of the app's flex column — so
       * the moment a screen grew taller than the viewport it scrolled away
       * with the page instead of staying under the thumb. Reported from a
       * phone on the Action tab, which is the longest list in the app.
       * `main` carries matching bottom padding so nothing hides behind it.
       */
      className="fixed inset-x-0 bottom-0 z-30 bg-ink text-paper flex justify-between px-4 py-3 pb-safe"
    >
      {tabs.map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => onTab(id!)}
          /*
           * `bc-tab` pulls the letter-spacing in. Five labels is the only place
           * `bc-label`'s tracking has to share a line with four other words, and
           * at 320px it does not fit — see the note beside the class in
           * index.css. It cannot be done with a `tracking-` utility here; the
           * per-theme `.bc-label` rules are unlayered and win.
           */
          className={`bc-label bc-tab bc-tap px-1.5 relative ${
            tab === id ? 'text-paper shadow-[0_3px_0_var(--color-onink-signal)]' : 'text-onink'
          }`}
        >
          {label}
          {/*
            Not shown on the tab you are already looking at — at that point the
            screen itself is the notice, and a dot that stays lit while you read
            the thing it points at is what teaches people to ignore dots.
          */}
          {flag === id && tab !== id && (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-onink-signal"
            />
          )}
        </button>
      ))}
    </nav>
  )
}

/**
 * Get it onto the home screen, said where somebody will actually see it.
 *
 * On iOS, Web Push reaches an installed app and nothing else — so birdie alerts
 * were unreachable for eleven people, and not because they refused: the only
 * sentence in the app that explained it sat inside the notifications row, on a
 * tab called Setup, below a PIN lock. Nobody was ever going to find it.
 *
 * Shown only while the app is running in a browser tab and is therefore NOT
 * installed, which is exactly the window in which the advice is actionable. It
 * disappears by itself the moment somebody follows it — the standalone check is
 * the same one `pushState` uses to decide whether push is possible at all.
 *
 * Dismissible, and remembered per device. A standing nag across the top of the
 * scoring screen would be worse than the problem: this has to be easy to make
 * go away for the man who has decided he does not want it.
 */
export function InstallPrompt() {
  const KEY = 'woodsmen:install-dismissed'
  const [gone, setGone] = useState(() => {
    try {
      return localStorage.getItem(KEY) === '1'
    } catch {
      return false
    }
  })
  if (gone || isStandalone()) return null

  const dismiss = () => {
    try {
      localStorage.setItem(KEY, '1')
    } catch {
      // A full quota is not a reason to keep shouting.
    }
    setGone(true)
  }

  return (
    <div className="bg-ink text-paper px-4 py-3 flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <p className="bc-label">Put it on your home screen</p>
        <p className="text-[13px] leading-snug mt-1 text-onink">
          {isApple() ? (
            <>
              Tap <b className="bc-name text-paper">Share</b>, then{' '}
              <b className="bc-name text-paper">Add to Home Screen</b>. Open it from there
              and birdie alerts will work — on an iPhone they only reach an installed app.
              It also stops the scoring screen losing your place.
            </>
          ) : (
            <>
              Install it from your browser menu. It opens full screen, keeps working with
              no signal, and can send birdie alerts.
            </>
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss the home screen tip"
        className="bc-tap bc-label border-2 border-paper px-3 py-1.5 shrink-0"
      >
        Not now
      </button>
    </div>
  )
}
